<template lang="pug">
  #team-members
    BLoading(:is-full-page="false" :active.sync="isLoading")
    .team-members-header.level.is-mobile(v-if="showTitle")
      .level-left
        .level-item
          WtTitle(card-title) Team Members
      .level-right
        .level-item
          WtRouterLink.is-size-7(
            to="TeamViewAll"
            :params="{ teamId: team.teamNameURL }"
          ) View All
    .block(v-if="!isTeamEdit")
      .empty-state(v-if="_.isEmpty(teamMembers)")
        p Hey there,
        p No team members yet.

      .team-members-gallery.px-1.mb-5(v-if="!_.isEmpty(teamMembers)")
        .columns.is-multiline.is-mobile
          template(v-for="member in teamMembers")
            .column.is-one-fifth-mobile.is-one-third-tablet.is-one-quarter-tablet.is-one-fifth-fullhd.has-tooltip
              WtRouterLink(
                to="ProfilePage"
                :params="{ username: member.username }"
              )
                EntityAvatar(:image="member.avatar" size-class="is-48x48")
                BIcon.star-for-leaders(
                  v-if="member.isTeamLeader"
                  size="is-small"
                  icon="star"
                  type="is-primary"
                )
                .username-tooltip.is-size-6 @{{ member.username }}
                  p.is-size-7.has-text-centered(v-if="member.isTeamLeader") Team Leader

    .block(v-if="isTeamEdit")
      WtEntityTable(
        title="Team Members"
        :showTitle="false"
        :data="teamMembers"
        defaultSortField="isTeamLeader"
        :empty-message="emptyMessageText"
        :searchFields=[ 'username' ]
        enable-search
        @search="onSearch"
        searchPlaceholder="Search by username..."
      )
        BTableColumn(field="username" label="Username" sortable v-slot="column")
          span.has-text-grey-dark {{ column.row.username }}
          .is-flex.is-align-items-center.mt-1(v-if="column.row.isTeamLeader")
            BIcon.padding-right(
              size="is-normal"
              icon="star"
              type="is-primary"
            )
            span.has-text-primary.is-size-7.has-text-weight-bold Team Leader
        BTableColumn(v-if="isTeamAdmin" field="isTeamLeader" sortable width="15rem" v-slot="column")
          WtButton(
            v-if="!column.row.isTeamLeader"
            isText
            bold
            @click.native="promoteTeamLeaderAction({teamId: team.id, memberId: column.row.id})"
          ) Make Team Leader
          WtButton(
            v-else
            isText
            bold
            color="red"
            @click.native="demoteTeamLeaderAction({teamId: team.id, memberId: column.row.id})"
          ) Remove Team Leader
        BTableColumn(v-if="isTeamAdmin" width="12rem" v-slot="column")
          WtButton(
            color="red"
            isText
            bold
            @click.native="removeMemberFromTeamAction({team: team, member: column.row})"
          ) REMOVE
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import EntityAvatar from "@/components/WtEntityAvatar/WtEntityAvatar";
import WtEntityTable from "@@/WtEntityTable/WtEntityTable";
import WtRouterLink from "@@/WtRouterLink/WtRouterLink";

export default {
  name: "TeamMembers",

  components: {
    WtButton,
    WtTitle,
    WtEntityTable,
    EntityAvatar,
    WtRouterLink
  },

  props: {
    team: {
      type: Object,
      required: true
    },

    teamMembers: {
      type: Array,
      required: true
    },

    showTitle: {
      type: Boolean,
      default: false
    },

    isTeamEdit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      teamMembersLoaded: false,
      isLoading: false,
      searching: false
    };
  },

  computed: {
    ...mapGetters("members", [
      "memberTeams",
      "teamsMemberIsAdmin",
      "teamsMemberIsAdminLoaded"
    ]),

    emptyMessageText() {
      return this.searching
        ? "No members found with that username."
        : "No members have been added yet.";
    },

    // team admin is either the creator of the team, or is a team leader
    isAuthUserTeamAdmin() {
      return (
        this.teamsMemberIsAdminLoaded &&
        !!this._.find(this.teamsMemberIsAdmin, { id: this.team.id })
      );
    },

    // the team leader can be someone other than the team creator
    isAuthUserTeamLeader() {
      const member = this._.find(this.originalTeam.members, {
        id: this.$auth.user().id
      });
      if (!member) return false;
      return member.isTeamLeader;
    },

    isTeamAdmin() {
      // auth-user created the team or is a team leader, or is program-admin or wt-admin
      return this.isAuthUserTeamAdmin || this.$auth.user().isAdmin;
    }
  },

  methods: {
    ...mapActions("teams", [
      "promoteTeamLeader",
      "demoteTeamLeader",
      "removeMembersFromTeam"
    ]),
    ...mapActions("pages", ["setPageLoaded"]),
    ...mapActions(["snackBar"]),

    async promoteTeamLeaderAction(payload) {
      this.$buefy.dialog.confirm({
        title: "Make Team Leader",
        confirmText: "Confirm",
        type: "is-primary",
        hasIcon: false,
        message:
          "Team Leaders are able to edit settings and manage members for this team.",
        onConfirm: async () => {
          this.isLoading = true;
          const response = await this.promoteTeamLeader(payload);
          if (response) {
            this.$set(
              this._.find(this.teamMembers, { id: payload.memberId }),
              "isTeamLeader",
              true
            );
          }
          this.isLoading = false;
        }
      });
    },

    async demoteTeamLeaderAction(payload) {
      this.$buefy.dialog.confirm({
        title: "Remove Team Leader",
        confirmText: "Confirm",
        type: "is-danger",
        hasIcon: true,
        message:
          "Are you sure you want to remove this Team Leader? They will no longer be able to edit settings or manage members for this team.",
        onConfirm: async () => {
          this.isLoading = true;
          const response = await this.demoteTeamLeader(payload);
          if (response) {
            this.$set(
              this._.find(this.teamMembers, { id: payload.memberId }),
              "isTeamLeader",
              false
            );
          }
          this.isLoading = false;
        }
      });
    },

    removeMemberFromTeamAction(payload) {
      const { team, member } = payload;

      this.$buefy.dialog.confirm({
        title: "Remove Member From This Team",
        confirmText: "Remove Member From This Team",
        type: "is-danger",
        hasIcon: true,
        message: "Are you sure you want to remove this member from this team?",
        onConfirm: async () => {
          this.isLoading = true;

          await this.removeMembersFromTeam({
            teamId: team.id,
            memberIds: [member.id]
          }).then(response => {
            if (response && response.data.success) {
              this.teamMembers = this.teamMembers.filter(
                m => m.id !== member.id
              );

              this.setPageLoaded({
                key: `MyDashboard`,
                value: false
              });
              this.setPageLoaded({
                key: `Teams`,
                value: false
              });

              this.snackBar({
                duration: 5000,
                type: "is-success",
                message: `${member.username} is no longer a member of team ${team.name}.`
              });

              this.$emit("memberRemoved", member.id);
            }
          });
          this.isLoading = false;
        }
      });
    },

    onSearch($event) {
      $event === "" ? (this.searching = false) : (this.searching = true);
    }
  }
};
</script>

<style lang="sass" scoped>
#team-members
  .team-members-gallery
    .media
      width: 0 // needed for IE11 fix
  .username-tooltip
    display: none
    position: absolute
    min-width: 90px
    left: 50%
    transform: translate(-50%, 0)
    z-index: 1
    background-color: $white
    padding: $size-7
    border-radius: $radius-large
    box-shadow: $box-shadow
  .has-tooltip
    position: relative
    font-size: $size-normal
    &:hover
      .username-tooltip
        display: block
  .star-for-leaders
    position: absolute
    left: 8px
    top: 8px
</style>
